import { changed, noOp, type CommandExecution, type LiveCommand } from './commands';
import { damage } from './damage';
import { invertMatrix, transformPoint, transformRectBoundsPrecise, type Point, type Rect } from './geometry';
import { findLayer, layerDocumentTransform, type EditorDocument, type LayerId } from './model';
import { canonicalizeRegions, combineRegions, projectRegionsToDocument, type SelectionCombineMode, type SelectionState } from './selection';
import type { RasterTileStore } from './tiles';

function rgbaAt(store: RasterTileStore, x: number, y: number): readonly [number, number, number, number] {
  if (x < 0 || y < 0 || x >= store.width || y >= store.height) return [0, 0, 0, 0];
  const tileX = Math.floor(x / store.tileSize);
  const tileY = Math.floor(y / store.tileSize);
  const pixels = store.getTile(tileX, tileY);
  if (!pixels) return [0, 0, 0, 0];
  const width = store.tileDimensions(tileX, tileY).width;
  const offset = ((y - tileY * store.tileSize) * width + (x - tileX * store.tileSize)) * 4;
  return [pixels[offset] ?? 0, pixels[offset + 1] ?? 0, pixels[offset + 2] ?? 0, pixels[offset + 3] ?? 0];
}

function colorDistance(left: readonly number[], right: readonly number[]): number {
  const red = (left[0] ?? 0) - (right[0] ?? 0);
  const green = (left[1] ?? 0) - (right[1] ?? 0);
  const blue = (left[2] ?? 0) - (right[2] ?? 0);
  return Math.sqrt(red * red * 0.299 + green * green * 0.587 + blue * blue * 0.114);
}

/** Bounded local flood fill used by the classic, non-AI Quick Selection brush. */
export function quickSelectionRegions(source: RasterTileStore, seed: Point, radius: number, tolerance: number, maximumPixels = 250_000): Rect[] {
  const centerX = Math.floor(seed.x);
  const centerY = Math.floor(seed.y);
  const seedColor = rgbaAt(source, centerX, centerY);
  if (seedColor[3] === 0) return [];
  const boundedRadius = Math.max(1, Math.min(512, Math.round(radius)));
  const threshold = Math.max(1, Math.min(255, tolerance));
  const edgeThreshold = Math.max(8, threshold * 0.72);
  const left = Math.max(0, centerX - boundedRadius);
  const top = Math.max(0, centerY - boundedRadius);
  const right = Math.min(source.width - 1, centerX + boundedRadius);
  const bottom = Math.min(source.height - 1, centerY + boundedRadius);
  const width = right - left + 1;
  const height = bottom - top + 1;
  const visited = new Uint8Array(width * height);
  const selected = new Uint8Array(width * height);
  const queueX = new Int32Array(Math.min(width * height, maximumPixels));
  const queueY = new Int32Array(queueX.length);
  let head = 0; let tail = 0;
  queueX[tail] = centerX; queueY[tail] = centerY; tail += 1;
  while (head < tail && head < maximumPixels) {
    const x = queueX[head] ?? centerX;
    const y = queueY[head] ?? centerY;
    head += 1;
    const local = (y - top) * width + x - left;
    if (visited[local]) continue;
    visited[local] = 1;
    const color = rgbaAt(source, x, y);
    if (color[3] === 0 || colorDistance(color, seedColor) > threshold) continue;
    selected[local] = 1;
    for (const [nextX, nextY] of [[x - 1, y], [x + 1, y], [x, y - 1], [x, y + 1]] as const) {
      if (nextX < left || nextY < top || nextX > right || nextY > bottom || (nextX - centerX) ** 2 + (nextY - centerY) ** 2 > boundedRadius ** 2) continue;
      const nextLocal = (nextY - top) * width + nextX - left;
      if (visited[nextLocal] || tail >= queueX.length) continue;
      if (colorDistance(rgbaAt(source, nextX, nextY), color) > edgeThreshold) continue;
      queueX[tail] = nextX; queueY[tail] = nextY; tail += 1;
    }
  }
  const regions: Rect[] = [];
  for (let y = 0; y < height; y += 1) {
    let start: number | null = null;
    for (let x = 0; x <= width; x += 1) {
      const on = x < width && selected[y * width + x] === 1;
      if (on && start === null) start = x;
      if (!on && start !== null) {
        regions.push({ x: left + start, y: top + y, width: x - start, height: 1 });
        start = null;
      }
    }
  }
  return canonicalizeRegions(regions);
}

export class QuickSelectionCommand implements LiveCommand {
  readonly label = 'Quick Selection stroke';
  private before?: SelectionState;
  private after?: SelectionState;
  private aggregate: Rect[] = [];
  private lastPoint?: Point;

  constructor(
    private readonly layerId: LayerId,
    private readonly firstPoint: Point,
    private readonly radius: number,
    private readonly tolerance: number,
    private readonly mode: SelectionCombineMode,
  ) {}

  retainedBytes(): number {
    return 256 + JSON.stringify(this.before ?? {}).length * 2 + JSON.stringify(this.after ?? {}).length * 2;
  }

  execute(document: EditorDocument): CommandExecution {
    this.before ??= structuredClone(document.selection);
    if (!this.addSample(document, this.firstPoint)) return noOp();
    return changed(this.damage());
  }

  append(document: EditorDocument, point: Point): void {
    if (this.lastPoint && Math.hypot(point.x - this.lastPoint.x, point.y - this.lastPoint.y) < Math.max(1, this.radius * 0.2)) return;
    this.addSample(document, point);
  }

  private addSample(document: EditorDocument, documentPoint: Point): boolean {
    const layer = findLayer(document.root, this.layerId);
    if (layer?.kind !== 'raster' || !this.before) return false;
    const transform = layerDocumentTransform(document.root, layer.id) ?? layer.transform;
    const inverse = invertMatrix(transform);
    if (!inverse) return false;
    const localPoint = transformPoint(inverse, documentPoint);
    const localRegions = quickSelectionRegions(layer.raster, localPoint, this.radius, this.tolerance);
    if (!localRegions.length) return false;
    // Same projection rule as Apply Image / Color Range / Luminosity Masks:
    // per-run bounding boxes are only exact while the layer is axis-aligned.
    // This runs inside a pointer handler, so an oversize layer must degrade to
    // the cheap approximation rather than throw out of the event.
    let projected: Rect[];
    try {
      projected = projectRegionsToDocument(localRegions, layer.raster, transform);
    } catch {
      projected = localRegions.map((region) => transformRectBoundsPrecise(transform, region));
    }
    this.aggregate = canonicalizeRegions([...this.aggregate, ...projected]);
    this.after = combineRegions(this.before, this.aggregate, this.mode);
    document.selection = structuredClone(this.after);
    this.lastPoint = documentPoint;
    return true;
  }

  seal(): CommandExecution {
    return this.after && JSON.stringify(this.before) !== JSON.stringify(this.after) ? changed(this.damage()) : noOp();
  }

  undo(document: EditorDocument): void {
    if (this.before) document.selection = structuredClone(this.before);
  }

  redo(document: EditorDocument): void {
    if (this.after) document.selection = structuredClone(this.after);
  }

  damage() { return damage({ fullDocument: true }); }
}
